/**
 * Recover from Vite stale-chunk failures after a portal redeploy.
 *
 * Long-lived tabs keep an old main bundle that still references content-hashed
 * lazy chunks (e.g. monaco `editor.main-*.js`). The first `import()` after a
 * deploy 404s with "Failed to fetch dynamically imported module". A full reload
 * picks up the new index/hashes. Same class of fix as VueBusinessPhone's
 * router `onError` chunk handler and Vite's `vite:preloadError` event.
 */

const RELOAD_TS_KEY = 'callcorp.vue3.vitePreloadReloadAt';
const COOLDOWN_MS = 15_000;

const CHUNK_LOAD_ERROR_RE =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i;

export function isLazyChunkLoadError(error) {
    const message =
        error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : String(error ?? '');
    return CHUNK_LOAD_ERROR_RE.test(message);
}

/**
 * @returns {boolean} true if a reload was scheduled (caller should stop handling)
 */
export function tryReloadForStaleChunk(error) {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined')
        return false;
    if (error != null && !isLazyChunkLoadError(error))
        return false;

    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
    if (last && now - last < COOLDOWN_MS)
        return false;

    sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    window.location.reload();
    return true;
}

/** Register once at boot (before mount). Covers every Vite dynamic `import()`. */
export function installVitePreloadRecovery() {
    if (typeof window === 'undefined')
        return;

    window.addEventListener('vite:preloadError', (event) => {
        const now = Date.now();
        const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
        if (last && now - last < COOLDOWN_MS)
            return;

        sessionStorage.setItem(RELOAD_TS_KEY, String(now));
        event.preventDefault();
        window.location.reload();
    });
}
